import React from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * Admin-only shell.
 * Frontend guard is a CONVENIENCE — the backend re-verifies role on every
 * /api/admin/* endpoint. Do not rely on this component for security.
 */
export default function AdminShell() {
  const { user, isChecking } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("common.loading")}
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.role !== "administrator") return <Navigate to="/app/discover" replace />;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-shell">
      <header
        className="border-b sticky top-0 z-30"
        style={{
          borderColor: "hsl(var(--nvl-border))",
          backgroundColor: "hsl(var(--nvl-surface))",
        }}
      >
        <div className="nvl-container py-4 flex items-center gap-6">
          <Link to="/admin/moderation" className="font-display text-lg tracking-tight flex items-center gap-2">
            <ShieldCheck size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
            NO VAX LOVE — Admin
          </Link>
          <nav className="flex-1 flex items-center gap-4 text-sm">
            <Link
              to="/admin/moderation"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-moderation"
            >
              {t("app.admin.nav.moderation")}
            </Link>
            <Link
              to="/admin/audit-log"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-audit"
            >
              {t("app.admin.nav.auditLog")}
            </Link>
            <Link
              to="/admin/statistics"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-statistics"
            >
              Statistiche
            </Link>
            <Link
              to="/admin/billing-config"
              className="underline-offset-4 hover:underline"
              data-testid="admin-nav-billing"
            >
              {t("app.admin.nav.billing")}
            </Link>
          </nav>
          <LanguageSwitcher compact />
          <Link
            to="/app/profile"
            className="nvl-btn nvl-btn-outline text-sm inline-flex items-center gap-2"
            data-testid="admin-back-to-app"
          >
            <ArrowLeft size={15} />
            {t("app.nav.profile")}
          </Link>
        </div>
      </header>
      <main className="flex-1 nvl-container py-8 md:py-12">
        <Outlet />
      </main>
    </div>
  );
}
